/**
 * `/segments/new` — naming a segment and choosing what goes in it.
 *
 * The screen is built around one idea: the effect of each choice should be
 * visible *before* it is saved. So the count panel sits beside the picker and
 * follows every tick, and it shows exactly one number — the size of the union —
 * because that is the only one that can honestly be known from here.
 *
 * The other two are refused on purpose. Whether two of these rows are the same
 * person depends on the addresses inside them, not on how many rows there are,
 * and the whole point of this slice is that a segment never advertises a number
 * it cannot stand behind. Saving computes all three from the contacts
 * themselves, and the segment opens on its own screen with them.
 *
 * A segment with no lists is a legal thing to create — a container someone is
 * about to fill — so the form does not force a selection. It says what will
 * happen instead.
 *
 * ## What the shorter copy kept
 *
 * The card descriptions used to explain, twice each, that a segment references
 * its lists rather than copying them. That argument is the paragraph above and
 * belongs to whoever changes this screen next, not to somebody filling in a
 * name. What stayed on the page is the pair of statements a person cannot
 * finish this form correctly without:
 *
 * - **Only one of the three counts can be known before saving.** Said by
 *   `ProjectedCountsPanel`, which shows the union size and refuses the other
 *   two rather than estimating them. The line under the submit button does not
 *   repeat it.
 * - **Saving with nothing ticked is legal and produces zeros.** Without it, an
 *   empty segment reads as a form that silently failed.
 *
 * ## Slice 66: the preview is the Blueprint's result panel, still in rows
 *
 * The right-hand column is `ProjectedCountsPanel` drawn as a brand
 * `MetricPanel`, with a bar per ticked list. Its figure is still **rows** —
 * "before duplicates" is in its line — because the concept's version of this
 * panel is a live "matches now" count of people, and that needs a
 * de-duplicating preview the API does not have. Drawing people here would be
 * the fabrication the paragraph above refuses.
 *
 * The list picker stays a table of checkboxes rather than the concept's
 * "build from" chips: its accessible names ("Include …", "Include every list
 * on this page"), its paging and its select-all are what make a selection of
 * forty lists possible, and a row of toggle chips has none of them.
 *
 * ## Slice 67: the page asks which kind first
 *
 * `/segments/new` now opens on `SegmentKindChoice` — "From uploads (static)"
 * or "By rules (dynamic)" — and `?type=rule` renders the rule builder
 * (`RuleSegmentBuilder.tsx`) instead of this form. The branch is a wrapper
 * rather than an `if` inside the static form, because the form's hooks would
 * otherwise run conditionally. Everything above about the static form is
 * unchanged; it is still the default, so every existing link to
 * `/segments/new` lands where it did.
 */
import {
  MAX_SEGMENT_DESCRIPTION_LENGTH,
  createSegmentRequestSchema,
  type SegmentDataset,
} from '@wend/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormField,
  Input,
  buttonVariants,
  useToast,
} from '@wend/ui';
import { ArrowLeft, Plus } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { AppLayout } from '../../components/AppLayout';
import { FormAlert } from '../../components/FormAlert';
import { ProjectedCountsPanel } from '../../components/SegmentCounts';
import { Textarea } from '../../components/Textarea';
import { ROOT_FIELD, apiFieldErrors, errorMessage, isApiError } from '../../lib/api/errors';
import { createSegment } from '../../lib/api/segments';
import { validate, withoutError, type FormErrors } from '../../lib/form';
import { pluralise } from '../../lib/format';
import { DatasetPicker, membershipError } from './DatasetPicker';
import { RuleSegmentBuilder } from './RuleSegmentBuilder';
import { SegmentKindChoice } from './SegmentKindChoice';

export function NewSegmentPage() {
  const [params] = useSearchParams();
  return params.get('type') === 'rule' ? <NewRuleSegmentPage /> : <NewStaticSegmentPage />;
}

/** `?type=rule` — the builder, under the same kind choice. */
function NewRuleSegmentPage() {
  return (
    <AppLayout
      title="New segment"
      actions={
        <Link to="/segments" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
          <ArrowLeft aria-hidden="true" className="size-4" />
          All segments
        </Link>
      }
    >
      <div className="flex flex-col gap-6">
        <SegmentKindChoice value="rule" />
        <RuleSegmentBuilder mode="create" />
      </div>
    </AppLayout>
  );
}

function NewStaticSegmentPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<SegmentDataset[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [requestId, setRequestId] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);

  const projectedContacts = selected.reduce((total, dataset) => total + dataset.contactCount, 0);
  const hasUnimported = selected.some((dataset) => dataset.status !== 'ready');

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (pending) return;

    setRequestId(undefined);

    // The same contract the API validates against, so a segment it could never
    // accept never leaves the browser.
    const result = validate(createSegmentRequestSchema, {
      name,
      description,
      datasetIds: selected.map((dataset) => dataset.id),
    });

    if (!result.ok) {
      setErrors(result.errors);
      return;
    }

    setErrors({});
    setPending(true);

    try {
      const segment = await createSegment(result.data);
      toast({
        tone: 'success',
        title: 'Segment created',
        description: `${segment.name} combines ${pluralise(segment.datasets.length, 'list')}.`,
      });
      // Straight to the segment, because the two counts this screen could not
      // work out are waiting there.
      navigate(`/segments/${segment.id}`, { replace: true });
    } catch (error) {
      setErrors({ ...apiFieldErrors(error), [ROOT_FIELD]: errorMessage(error) });
      setRequestId(isApiError(error) ? error.requestId : undefined);
      setPending(false);
    }
  }

  const selectionError = membershipError(errors);

  return (
    <AppLayout
      title="New segment"
      actions={
        <Link to="/segments" className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
          <ArrowLeft aria-hidden="true" className="size-4" />
          All segments
        </Link>
      }
    >
      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        noValidate
        className="flex flex-col gap-6"
      >
        <SegmentKindChoice value="static" />

        {errors[ROOT_FIELD] !== undefined && (
          <FormAlert tone="error" requestId={requestId}>
            {errors[ROOT_FIELD]}
          </FormAlert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Name this segment</CardTitle>
            {/* Sentence two was mechanism, above a name field, with no bearing
                on what to type. Slice 29. */}
            <CardDescription>A segment is who you are going to send to.</CardDescription>
          </CardHeader>

          <CardContent className="flex max-w-xl flex-col gap-5">
            <FormField label="Name" required error={errors.name}>
              <Input
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setErrors((current) => withoutError(current, 'name'));
                }}
                placeholder="Diwali — customers with an email"
                autoComplete="off"
                disabled={pending}
              />
            </FormField>

            <FormField
              label="Description"
              description={`Optional. Why this group exists, for whoever opens it next. Up to ${String(MAX_SEGMENT_DESCRIPTION_LENGTH)} characters.`}
              error={errors.description}
            >
              <Textarea
                rows={3}
                value={description}
                maxLength={MAX_SEGMENT_DESCRIPTION_LENGTH}
                onChange={(event) => {
                  setDescription(event.target.value);
                  setErrors((current) => withoutError(current, 'description'));
                }}
                disabled={pending}
              />
            </FormField>
          </CardContent>
        </Card>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start">
          <Card>
            <CardHeader>
              <CardTitle>Choose the lists</CardTitle>
            {/* Sentence one described what a column of checkboxes means.
                Sentence two is the real content — the de-duplication promise, at
                the moment the lists are chosen. Slice 29. */}
              <CardDescription>
                Somebody on two of them is in it once when a campaign sends.
              </CardDescription>
            </CardHeader>

            <CardContent className="flex flex-col gap-4">
              {selectionError !== undefined && (
                <FormAlert tone="error">{selectionError}</FormAlert>
              )}

              <DatasetPicker selected={selected} onChange={setSelected} disabled={pending} />
            </CardContent>
          </Card>

          {/* The panel is its own card: a brand MetricPanel inside a Card was
              two borders around one number. Its kicker carries the title the
              Card used to — as an h2, as the CardTitle was, so the projection
              stays in the heading outline. Slice 66. */}
          <ProjectedCountsPanel
            className="lg:sticky lg:top-20"
            contacts={projectedContacts}
            datasetCount={selected.length}
            hasUnimported={hasUnimported}
            selected={selected}
            kickerAs="h2"
          />
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          <Button type="submit" loading={pending}>
            <Plus aria-hidden="true" className="size-4" />
            {pending ? 'Creating the segment' : 'Create segment'}
          </Button>

          <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
            {/* The ticked variant duplicated the projected panel two inches to
                the right, which has just said the same thing at greater length.
                The empty variant stays: it prevents an empty save reading as a
                silent failure. Slice 29. */}
            {selected.length === 0
              ? 'Nothing is ticked, so this saves an empty segment reporting zeros. You can add lists at any time.'
              : ''}
          </p>
        </div>
      </form>
    </AppLayout>
  );
}
